import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HiX, HiExternalLink } from 'react-icons/hi';

const CreditsModal = ({ isOpen, onClose }) => {
  // Blocca lo scroll del body quando la modale è aperta
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Chiudi con Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const linkStyle = {
    color: 'var(--primary-color)',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  };

  const ExternalLink = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={linkStyle}
      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
    >
      {children}
      <HiExternalLink style={{ fontSize: '0.875rem', flexShrink: 0 }} />
    </a>
  );

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: '16px',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--background-color)',
          border: '1px solid var(--border-color)',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
          animation: 'slideUp 0.25s ease',
        }}
      >
        {/* Header fisso */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '28px 32px 24px',
          borderBottom: '1px solid var(--border-color)',
          position: 'sticky',
          top: 0,
          background: 'var(--background-color)',
          zIndex: 1,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Credits
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              transition: 'color 0.2s, border-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--primary-color)';
              e.currentTarget.style.borderColor = 'var(--primary-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.borderColor = 'var(--border-color)';
            }}
            aria-label="Close"
          >
            <HiX />
          </button>
        </div>

        {/* Contenuto scrollabile */}
        <div style={{ padding: '32px' }}>
          <p style={{
            fontSize: '1rem',
            color: 'var(--text-secondary)',
            marginBottom: '32px',
            lineHeight: 1.6,
          }}>
            Credits for the resources used in this portfolio.
          </p>

          {/* Retro Monitor */}
          <section style={{
            background: 'var(--card-background)',
            padding: '28px',
            marginBottom: '24px',
            border: '1px solid var(--border-color)',
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '20px',
            }}>
              3D Model — Retro Monitor
            </h3>

            <dl style={{ margin: 0, lineHeight: 2, color: 'var(--text-secondary)' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <dt style={{ fontWeight: '600', color: 'var(--text-primary)', minWidth: '80px' }}>Title:</dt>
                <dd style={{ margin: 0 }}>Retro Monitor — IBM/Commodore style monitor</dd>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <dt style={{ fontWeight: '600', color: 'var(--text-primary)', minWidth: '80px' }}>Author:</dt>
                <dd style={{ margin: 0 }}>
                  <ExternalLink href="https://sketchfab.com/Smoothie3D">Smoothie 3D</ExternalLink>
                </dd>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <dt style={{ fontWeight: '600', color: 'var(--text-primary)', minWidth: '80px' }}>License:</dt>
                <dd style={{ margin: 0 }}>
                  <ExternalLink href="http://creativecommons.org/licenses/by/4.0/">CC-BY-4.0</ExternalLink>
                </dd>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <dt style={{ fontWeight: '600', color: 'var(--text-primary)', minWidth: '80px' }}>Source:</dt>
                <dd style={{ margin: 0 }}>
                  <ExternalLink href="https://skfb.ly/oH6XP">Sketchfab — Retro Monitor</ExternalLink>
                </dd>
              </div>
            </dl>

            <div style={{
              background: 'var(--surface-color)',
              padding: '16px',
              marginTop: '20px',
              borderLeft: '3px solid var(--primary-color)',
            }}>
              <p style={{ fontSize: '0.8125rem', lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0 }}>
                <strong style={{ color: 'var(--text-primary)' }}>Attribution: </strong>
                This work is based on "Retro Monitor — IBM/Commodore style monitor" by Smoothie 3D, licensed under CC-BY-4.0.
              </p>
            </div>
          </section>

          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: '0.8125rem',
            marginTop: '8px',
            marginBottom: 0,
          }}>
            All credits have been attributed according to license requirements.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CreditsModal;
